import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, Menu, X, ArrowRight, Check, Star, Zap, Users, MessageSquare, Calendar, BarChart2, Sparkles, Repeat, Bot, Target, Rocket, Award, Heart, Smile, Frown, Meh, ChevronRight, Twitter, Facebook, Instagram, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import TestimonialCarousel from '../components/TestimonialCarousel';
import { SparklesTextDemo } from '../components/ui/SparklesTextDemo';
import { DisplayCardsDemo } from '../components/ui/DisplayCardsDemo';
import { ComparisonDemo } from '../components/ui/ComparisonDemo';
import { Pricing } from '../components/ui/pricing';
import Header from '../components/Header';

// Define features array
const features = [
  {
    icon: Sparkles,
    title: "AI-Powered Content Creation",
    description: "Generate engaging posts with our advanced AI that understands your brand voice and audience preferences."
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description: "Automatically schedule posts at optimal times when your audience is most active."
  },
  {
    icon: BarChart2,
    title: "Analytics & Insights",
    description: "Track performance metrics and get AI-driven recommendations to improve engagement."
  },
  {
    icon: Repeat,
    title: "Content Repurposing",
    description: "Automatically adapt content for different platforms while maintaining your message."
  },
  {
    icon: Users,
    title: "Audience Analysis",
    description: "Understand your followers better with detailed demographic and engagement insights."
  },
  {
    icon: MessageSquare,
    title: "Engagement Management",
    description: "Monitor and respond to comments and messages across all platforms from one dashboard."
  }
];

// Pricing plans data
const pricingPlans = [
  {
    name: "FREE",
    price: "0",
    yearlyPrice: "0",
    period: "month",
    features: [
      "2 social media accounts",
      "Basic content calendar",
      "10 AI credits per month",
      "Community support",
      "Standard analytics"
    ],
    description: "Great for individuals just getting started",
    buttonText: "Get Started",
    href: "/signup",
    isPopular: false,
  },
  {
    name: "CHARGED",
    price: "19",
    yearlyPrice: "15",
    period: "month",
    features: [
      "5 social media accounts",
      "Advanced content calendar",
      "100 AI credits per month",
      "Priority email support",
      "Advanced analytics",
      "Content repurposing",
      "Scheduled posting"
    ],
    description: "Perfect for creators and small businesses",
    buttonText: "Start Free Trial",
    href: "/signup",
    isPopular: true,
  },
  {
    name: "SUPERCHARGED",
    price: "49",
    yearlyPrice: "39",
    period: "month",
    features: [
      "Unlimited social accounts",
      "Team collaboration",
      "Unlimited AI credits",
      "24/7 priority support",
      "Custom branding",
      "Advanced automation",
      "API access",
      "White-label reports"
    ],
    description: "For agencies and power users",
    buttonText: "Contact Sales",
    href: "/contact",
    isPopular: false,
  }
];

// Sample testimonials
const testimonials = [
  {
    quote: "Serge has completely transformed how I manage my social media presence. The AI-powered tools save me hours every week.",
    author: "Sarah Johnson",
    role: "Content Creator",
    avatar: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150"
  },
  {
    quote: "The multi-platform scheduling feature is a game-changer. I've seen a 40% increase in engagement since using Serge.",
    author: "Michael Chen",
    role: "Digital Marketer",
    avatar: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150"
  },
  {
    quote: "Finally, a tool that understands the needs of modern content creators. The analytics insights are invaluable.",
    author: "Emma Davis",
    role: "Social Media Manager",
    avatar: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150"
  }
];

// Define workflowSteps array
const workflowSteps = [
  {
    icon: Cloud,
    title: "Connect Accounts",
    description: "Link your social media accounts in one place"
  },
  {
    icon: ArrowRight,
    title: "Create Content",
    description: "Use AI-powered tools to generate engaging posts"
  },
  {
    icon: Check,
    title: "Schedule Posts",
    description: "Set optimal posting times across platforms"
  },
  {
    icon: Target,
    title: "Track Results",
    description: "Monitor performance and engagement metrics"
  }
];

// Feature Card Component
const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-dark-700 hover:shadow-xl transition-shadow"
    >
      <div className="h-12 w-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
    </motion.div>
  );
};

const LandingPage = () => {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const phrases = [
    "More Followers.",
    "More Sales.",
    "Zero Tab-Hopping."
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhrase((prev) => (prev + 1) % phrases.length);
    }, 2000); // Change phrase every 2 seconds
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-dark-900 dark:to-dark-800">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-5" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="text-center space-y-8 max-w-4xl mx-auto">
            <div className="h-[200px] sm:h-[240px] md:h-[280px] flex flex-col items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.h1 
                  key={currentPhrase}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-5xl sm:text-6xl md:text-7xl font-bold text-indigo-600 dark:text-indigo-400 leading-tight"
                >
                  {phrases[currentPhrase]}
                </motion.h1>
              </AnimatePresence>
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-medium text-gray-700 dark:text-gray-300">
              for <span className="text-indigo-600 dark:text-indigo-400">Content Creators</span>
            </h3>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Serge schedules, optimizes, and monetizes every post while you sleep.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Link
                to="/signup"
                className="group relative overflow-hidden btn-primary px-8 py-3 text-base"
              >
                <span className="relative z-10">Get Started Free</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-indigo-800"
                  initial={false}
                  animate={{ x: "100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
                <ArrowRight className="relative z-10 ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <button
                onClick={() => setShowSignInModal(true)}
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 bg-gray-50 dark:bg-dark-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Features</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Everything you need to manage your social media presence
            </p>
          </div>

          <DisplayCardsDemo />
        </div>
      </section>

      {/* How We Compare */}
      <section className="py-20 bg-gray-50 dark:bg-dark-900/50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900 dark:text-white">
            How We Compare
          </h2>
          <p className="text-xl text-center mb-12 text-gray-600 dark:text-gray-400">
            See why creators choose Serge over traditional solutions
          </p>
          <ComparisonDemo />
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50 dark:bg-dark-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Our platform streamlines your content creation workflow in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="bg-white dark:bg-dark-800 rounded-xl p-8 shadow-lg border border-gray-100 dark:border-dark-700 relative">
              <div className="absolute -top-5 -left-5 w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xl font-bold">1</div>
              <div className="mb-6 h-12 flex items-center justify-center">
                <Bot className="h-10 w-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Connect Your Accounts</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Link your social media accounts to Serge and let our AI analyze your content and audience.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white dark:bg-dark-800 rounded-xl p-8 shadow-lg border border-gray-100 dark:border-dark-700 relative mt-8 md:mt-0">
              <div className="absolute -top-5 -left-5 w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xl font-bold">2</div>
              <div className="mb-6 h-12 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Create & Schedule</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Use our AI-powered tools to create engaging content and schedule it for optimal times.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white dark:bg-dark-800 rounded-xl p-8 shadow-lg border border-gray-100 dark:border-dark-700 relative mt-8 md:mt-0">
              <div className="absolute -top-5 -left-5 w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xl font-bold">3</div>
              <div className="mb-6 h-12 flex items-center justify-center">
                <BarChart2 className="h-10 w-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Track & Optimize</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Monitor performance metrics and get AI-driven insights to improve your content strategy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white dark:bg-dark-800">
        <div className="container mx-auto px-4">
          <Pricing 
            plans={pricingPlans}
            title="Simple, Transparent Pricing"
            description="Choose the plan that works for you. All plans include access to our platform, content creation tools, and dedicated support."
          />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50 dark:bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Powerful Features</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Everything you need to create, manage, and optimize your content across all platforms
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">What Our Users Say</h2>
            <p className="text-xl">Join thousands of satisfied content creators</p>
          </div>
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-900 dark:to-purple-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Content Strategy?
            </h2>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
              Join thousands of content creators who are already using Serge to grow their audience
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/signup"
                className="group px-8 py-3 bg-white text-indigo-600 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center"
              >
                <Rocket className="mr-2 h-5 w-5" />
                <span>Start Creating Now</span>
                <ChevronRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/login"
                className="px-8 py-3 bg-transparent text-white font-medium rounded-lg border border-white hover:bg-white/10 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-800 py-12 border-t border-gray-100 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Cloud className="h-8 w-8 text-indigo-600" />
                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                  Serge
                </span>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Empowering content creators with AI-driven tools
              </p>
              <div className="flex space-x-4 pt-2">
                <a href="#" className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Features</a></li>
                <li><a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Pricing</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">API</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Integrations</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">About</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Blog</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Careers</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Press</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Partners</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Privacy Policy</Link></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Terms</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Security</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Cookies</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">GDPR</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-100 dark:border-dark-700 text-center text-gray-600 dark:text-gray-300">
            <p>&copy; {new Date().getFullYear()} Serge. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Sign In Modal */}
      <AnimatePresence>
        {showSignInModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-50"
              onClick={() => setShowSignInModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Sign In</h3>
                  <button
                    onClick={() => setShowSignInModal(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <form className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:border-dark-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:border-dark-600 dark:text-white sm:text-sm"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Remember me
                      </label>
                    </div>
                    <a href="#" className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      Forgot password?
                    </a>
                  </div>
                  <Link
                    to="/login"
                    className="w-full btn-primary py-2 flex items-center justify-center"
                  >
                    Sign In
                  </Link>
                  <div className="text-center mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Don't have an account?{' '}
                      <Link to="/signup" className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 font-medium">
                        Sign up
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Add shake animation for DIY and Agency cards
const style = document.createElement('style');
style.textContent = `
@keyframes shake {
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
}

.hover\\:shake:hover {
  animation: shake 0.5s ease-in-out;
}
`;
document.head.appendChild(style);

export default LandingPage;